import React from "react";
import { MsalProvider, AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from "@azure/msal-react";
import { IPublicClientApplication } from "@azure/msal-browser";
import { BrowserRouter, Routes, Route, Outlet, useSearchParams } from "react-router-dom";
import { loginRequest } from "./auth/msalConfig";
import AppLayout from "./components/AppLayout";
import { MeProvider } from "./contexts/MeContext";
import HomePage from "./pages/HomePage";
import GroupsRosterPage from "./pages/GroupsRosterPage";
import AdminToolsPage from "./pages/AdminToolsPage";
import AdminSettingsPage from "./pages/AdminSettingsPage";
import AdminCyclesPage from "./pages/AdminCyclesPage";
import AdminCycleDetailPage from "./pages/AdminCycleDetailPage";
import ReviewPage from "./pages/ReviewPage";
import NotFoundPage from "./pages/NotFoundPage";

const LoginPrompt: React.FC = () => {
  const { instance } = useMsal();
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      justifyContent: "center", height: "100vh", fontFamily: "Segoe UI, sans-serif",
      background: "#f3f3f3",
    }}>
      <div style={{
        background: "white", borderRadius: "8px", padding: "48px 56px",
        textAlign: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.08)", maxWidth: "400px",
      }}>
        <div style={{ fontSize: "36px", marginBottom: "20px" }}>🔐</div>
        <button
          onClick={() => instance.loginRedirect({
            ...loginRequest,
            redirectStartPage: window.location.href,
          })}
          style={{
            background: "#0078d4", color: "white", border: "none",
            padding: "12px 32px", fontSize: "15px", borderRadius: "4px",
            cursor: "pointer", width: "100%",
          }}
        >
          Sign in using SSO
        </button>
      </div>
    </div>
  );
};

/**
 * Dispatcher for the `/` route. Existing Slack notifications link to
 *   `${WEB_APP_URL}?tool=X&cycle=Y&stage=1`
 * which goes to `/` with query parameters — that flow has to keep working
 * unchanged. So if `tool` and `cycle` are both present, we render the
 * ReviewPage (the original behavior). Otherwise we show the HomePage.
 */
const RootRoute: React.FC = () => {
  const [params] = useSearchParams();
  if (params.get("tool") && params.get("cycle")) {
    return <ReviewPage />;
  }
  return <HomePage />;
};

const App: React.FC<{ msalInstance: IPublicClientApplication }> = ({ msalInstance }) => {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthenticatedTemplate>
        <MeProvider>
          <BrowserRouter>
            <Routes>
              {/* All authenticated routes share the AppLayout chrome (top bar + sign out). */}
              <Route element={<AppLayout><Outlet /></AppLayout>}>
                <Route path="/tools/:toolName/groups" element={<GroupsRosterPage />} />
                <Route path="/manage/tools" element={<AdminToolsPage />} />
                <Route path="/manage/settings" element={<AdminSettingsPage />} />
                <Route path="/manage/cycles" element={<AdminCyclesPage />} />
                <Route path="/manage/cycles/:cycleId" element={<AdminCycleDetailPage />} />
                <Route path="/" element={<RootRoute />} />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </MeProvider>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginPrompt />
      </UnauthenticatedTemplate>
    </MsalProvider>
  );
};

export default App;
